import {
  FONT_CONTENT_TYPES,
  FONT_FILE_CONFLICT_MESSAGE,
  FONT_NAME_CONFLICT_MESSAGE,
  FONT_UPLOAD_EXPIRED_MESSAGE,
  type FontFormat,
} from '@repo/api-contracts';
import { useRef, useState } from 'react';
import { useInvalidateFonts } from '@/entities/font';
import { describeCause, errorCodeOf, trpc } from '@/shared/api';
import { checkFontFile } from './font-file';

export type CreateFontPhase = 'idle' | 'uploading' | 'registering';

interface CreateFontInput {
  readonly file: File;
  readonly familyName: string;
}

interface UseCreateFontOptions {
  readonly onSuccess?: () => void;
}

interface CreateFontState {
  readonly phase: CreateFontPhase;
  /** 모달 위 오류 — 파일 검사·발급·PUT·등록 실패. */
  readonly error: string | null;
  /** 이름 칸 오류 — 살아 있는 폰트와 이름이 겹친다. */
  readonly nameError: string | null;
}

/** 올려 둔 임시 파일 — 같은 파일로 다시 등록하면 발급·PUT 없이 이 fontId 로 create 를 다시 부른다. */
interface UploadedFont {
  readonly file: File;
  readonly fontId: string;
  readonly format: FontFormat;
}

/** 등록(create) 실패를 다루는 방법. */
interface RegisterFailure {
  /** 올린 임시 파일을 두고 같은 fontId 로 다시 등록할 수 있는가. */
  readonly keepsUpload: boolean;
  /** 앞선 등록이 실제로는 끝났을 수 있어 목록을 다시 불러야 하는가. */
  readonly isListStale: boolean;
  readonly error: string | null;
  readonly nameError: string | null;
}

const IDLE: CreateFontState = { phase: 'idle', error: null, nameError: null };

/** 원인 뒤에 안내 문장을 잇는다 — 브라우저 원인('Failed to fetch' 등)은 마침표로 끝나지 않아 한 문장으로 붙지 않게 넣는다. */
const asSentence = (text: string): string => (/[.!?]$/.test(text) ? text : `${text}.`);

/**
 * 등록 거부를 가른다. 만료(410)는 BAD_REQUEST, 409 두 종은 같은 CONFLICT 로 오므로 코드가 아니라 계약 문구로 대조한다.
 * 올린 파일을 두는 경우는 허용 목록(이름 충돌·503·통신 오류)뿐이다 — 계약 상수가 아닌 409(다른 내용의 파일)·400 은
 * 같은 fontId 로 다시 불러도 같은 결과라 새로 올린다. 통신 오류는 등록이 실제로 끝났을 수 있는데, 그 재시도는
 * FONT_FILE_CONFLICT_MESSAGE 로 돌아와 목록 확인을 안내한다.
 */
const registerFailureOf = (cause: unknown): RegisterFailure => {
  const message = describeCause(cause);
  const code = errorCodeOf(cause);

  if (message === FONT_NAME_CONFLICT_MESSAGE) {
    return { keepsUpload: true, isListStale: false, error: null, nameError: message };
  }
  if (code === null || code === 'SERVICE_UNAVAILABLE') {
    const error = `등록 확인 실패: ${asSentence(message)} 같은 파일로 다시 시도할 수 있습니다.`;
    return { keepsUpload: true, isListStale: false, error, nameError: null };
  }
  if (message === FONT_UPLOAD_EXPIRED_MESSAGE) {
    return { keepsUpload: false, isListStale: false, error: message, nameError: null };
  }
  if (message === FONT_FILE_CONFLICT_MESSAGE) {
    const error = `${message} 앞선 등록이 이미 끝났을 수 있으니 목록을 확인해 주세요.`;
    return { keepsUpload: false, isListStale: true, error, nameError: null };
  }
  return {
    keepsUpload: false,
    isListStale: false,
    error: `등록 확인 실패: ${message}`,
    nameError: null,
  };
};

/** 임시 키에 PUT 한다. 성공하면 null, 실패하면 화면 문구. content-type 은 서명에 든 계약 표 값이어야 한다. */
const putFontFile = async (
  uploadUrl: string,
  file: File,
  format: FontFormat,
): Promise<string | null> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': FONT_CONTENT_TYPES[format] },
      body: file,
    });
    return response.ok ? null : `파일 올리기 실패 (HTTP ${response.status}). 다시 시도해 주세요.`;
  } catch (cause) {
    return `파일 올리기 실패: ${asSentence(describeCause(cause))} 다시 시도해 주세요.`;
  }
};

/**
 * 폰트 파일을 올리고 등록한다 — 발급(presignUpload) → 임시 키 PUT → 등록(create). 결정 F8.
 * 형식은 확장자로 정하고 PUT content-type 은 FONT_CONTENT_TYPES[형식]이다(file.type 을 쓰지 않는다).
 * 이름 충돌·503·통신 오류 뒤에는 올린 파일을 두어, 다시 등록하면 같은 fontId 로 create 만 다시 부른다.
 */
export const useCreateFont = ({ onSuccess }: UseCreateFontOptions = {}) => {
  const presign = trpc.productManagement.fonts.presignUpload.useMutation();
  const create = trpc.productManagement.fonts.create.useMutation();
  const invalidate = useInvalidateFonts();
  const [state, setState] = useState<CreateFontState>(IDLE);
  const uploaded = useRef<UploadedFont | null>(null);
  const isRunning = useRef(false);

  const upload = async (file: File, format: FontFormat): Promise<UploadedFont | null> => {
    setState({ phase: 'uploading', error: null, nameError: null });
    const signed = await presign.mutateAsync({ format }).catch((cause: unknown) => {
      setState({ ...IDLE, error: `업로드 준비 실패: ${describeCause(cause)}` });
      return null;
    });
    if (signed === null) return null;

    const putError = await putFontFile(signed.uploadUrl, file, format);
    if (putError !== null) {
      setState({ ...IDLE, error: putError });
      return null;
    }
    return { file, fontId: signed.fontId, format };
  };

  const register = async (target: UploadedFont, familyName: string): Promise<boolean> => {
    setState({ phase: 'registering', error: null, nameError: null });
    try {
      await create.mutateAsync({ fontId: target.fontId, format: target.format, familyName });
      uploaded.current = null;
      setState(IDLE);
      onSuccess?.();
      return true;
    } catch (cause) {
      const failure = registerFailureOf(cause);
      uploaded.current = failure.keepsUpload ? target : null;
      if (failure.isListStale) invalidate.list();
      setState({ phase: 'idle', error: failure.error, nameError: failure.nameError });
      return false;
    }
  };

  /** 성공하면 true — 업로드 모달을 닫는 데 쓴다. 진행 중에 다시 부르면 무시하고 false. */
  const createFont = async ({ file, familyName }: CreateFontInput): Promise<boolean> => {
    if (isRunning.current) return false;
    const checked = checkFontFile(file);
    if (!checked.ok) {
      setState({ ...IDLE, error: checked.message });
      return false;
    }

    isRunning.current = true;
    try {
      // 다른 파일을 고르면 이전에 올린 파일은 버린다(임시 객체는 수명주기가 지운다).
      const reusable = uploaded.current?.file === file ? uploaded.current : null;
      uploaded.current = reusable;
      const target = reusable ?? (await upload(file, checked.format));
      if (target === null) return false;
      uploaded.current = target;
      return await register(target, familyName);
    } finally {
      isRunning.current = false;
    }
  };

  return {
    createFont,
    phase: state.phase,
    isPending: state.phase !== 'idle',
    error: state.error,
    nameError: state.nameError,
    /** 지난 오류와 올린 파일을 버린다 — 업로드 모달을 새로 열거나 닫을 때 쓴다. 진행 단계는 그대로 둔다. */
    reset: (): void => {
      uploaded.current = null;
      setState((current) => ({ ...current, error: null, nameError: null }));
    },
  };
};
